import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { api } from "../../lib/api";

const FILTERS = ["all", "confirmed", "completed", "cancelled", "pending_payment"];

export default function BookingsTable({ onChange }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("confirmed");
  const [q, setQ] = useState("");
  const [links, setLinks] = useState({});

  const load = useCallback(() => {
    api.get("/admin/bookings", { params: filter === "all" ? {} : { status: filter } }).then(r => setRows(r.data));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body, msg) => {
    try { await api.patch(`/admin/bookings/${id}`, body); toast.success(msg); load(); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const markReports = async (id) => { await api.patch(`/admin/bookings/${id}/mark-reports-received`); toast.success("Marked reports received"); load(); onChange?.(); };

  const shown = rows.filter(r => !q || `${r.patient_name} ${r.patient_email} ${r.patient_phone} ${r.id}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="mt-6">
      <div className="flex flex-wrap gap-2 items-center">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`px-4 py-1.5 rounded-full text-sm border ${filter === f ? "bg-teal-700 text-white border-teal-700" : "bg-white border-slate-200 text-slate-700"}`}>{f.replace("_", " ")}</button>
        ))}
        <Input placeholder="Search name, email, phone…" value={q} onChange={e => setQ(e.target.value)} className="ml-auto max-w-xs" data-testid="bookings-search-input" />
      </div>
      <div className="mt-5 space-y-4" data-testid="bookings-list">
        {!shown.length && <p className="text-slate-500 text-sm py-8" data-testid="no-bookings">No bookings in this view.</p>}
        {shown.map(b => (
          <div key={b.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`booking-row-${b.id.slice(0, 8)}`}>
            <div className="flex flex-wrap justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900">{b.patient_name} <span className="text-xs font-mono text-slate-400 ml-2">{b.id.slice(0, 8).toUpperCase()}</span></div>
                <div className="text-sm text-slate-600">{b.ist_date} · {b.ist_time} IST · {b.consultation_type === "new" ? "New" : "Follow-up"} · ₹{b.amount_inr}</div>
                <div className="text-sm text-slate-500">{b.patient_email} · {b.patient_phone} · {b.patient_city}, {b.patient_country}</div>
              </div>
              <div className="flex gap-2 items-start">
                <Badge variant="outline" className="capitalize">{b.status.replace("_", " ")}</Badge>
                <Badge className={b.reports_received ? "bg-emerald-600" : "bg-amber-500"}>{b.reports_received ? "Reports in" : "Reports pending"}</Badge>
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-700 bg-slate-50 rounded-lg p-3">{b.reason}</p>
            {b.status === "confirmed" && (
              <div className="mt-4 flex flex-wrap gap-2 items-center">
                <Input placeholder="https://meet.google.com/… or Zoom link" defaultValue={b.meeting_link || ""} onChange={e => setLinks({ ...links, [b.id]: e.target.value })} className="flex-1 min-w-[240px]" data-testid={`meeting-link-input-${b.id.slice(0, 8)}`} />
                <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white" onClick={() => patch(b.id, { meeting_link: links[b.id] ?? b.meeting_link }, "Link saved & emailed")} data-testid={`save-link-btn-${b.id.slice(0, 8)}`}>Save & email link</Button>
                {!b.reports_received && <Button size="sm" variant="outline" onClick={() => markReports(b.id)} data-testid={`reports-btn-${b.id.slice(0, 8)}`}>Reports received</Button>}
                <Button size="sm" variant="outline" onClick={() => patch(b.id, { status: "completed" }, "Marked completed")} data-testid={`complete-btn-${b.id.slice(0, 8)}`}>Complete</Button>
                <Button size="sm" variant="outline" className="text-red-700" onClick={() => window.confirm("Cancel this booking and free the slot?") && patch(b.id, { status: "cancelled" }, "Booking cancelled")} data-testid={`cancel-btn-${b.id.slice(0, 8)}`}>Cancel</Button>
                <a href={`https://wa.me/${b.patient_phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid={`wa-patient-btn-${b.id.slice(0, 8)}`}>WhatsApp patient</Button></a>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
